import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TimeLogModal = ({ isOpen, onClose, project, onTimeLogged }) => {
  const [timeLog, setTimeLog] = useState({
    project_id: '',
    milestone_id: '',
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isOpen && project) {
      setTimeLog({
        project_id: project.id,
        milestone_id: project.milestones?.[0]?.id || '',
        hours: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
  }, [isOpen, project]);

  const handleLogTime = async () => {
    if (!timeLog.project_id || !timeLog.hours) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/time-logs`, {
        project_id: timeLog.project_id,
        milestone_id: timeLog.milestone_id || null,
        hours: parseFloat(timeLog.hours),
        description: timeLog.description,
        date: timeLog.date
      });

      toast.success('Time logged successfully');
      
      if (onTimeLogged) {
        onTimeLogged();
      }
      
      onClose();
    } catch (error) {
      console.error('Error logging time:', error);
      toast.error('Failed to log time');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Log Time</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X size={20} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Project</label>
            <Input 
              value={project?.project_name || ''}
              disabled
              className="mt-1 bg-accent"
            />
          </div>

          {project?.milestones && project.milestones.length > 0 && (
            <div>
              <label className="text-sm font-medium">Milestone (Optional)</label>
              <select 
                className="w-full mt-1 px-3 py-2 border border-input rounded-sm bg-background"
                value={timeLog.milestone_id}
                onChange={(e) => setTimeLog({...timeLog, milestone_id: e.target.value})}
              >
                <option value="">General Project Time</option>
                {project.milestones.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.description} ({m.hours_spent || 0}h / {m.hours_estimated}h)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Hours *</label>
            <Input 
              type="number" 
              step="0.5"
              min="0.5"
              value={timeLog.hours}
              onChange={(e) => setTimeLog({...timeLog, hours: e.target.value})}
              placeholder="Enter hours worked"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <Input 
              value={timeLog.description}
              onChange={(e) => setTimeLog({...timeLog, description: e.target.value})}
              placeholder="What did you work on?"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Date</label>
            <Input 
              type="date"
              value={timeLog.date}
              onChange={(e) => setTimeLog({...timeLog, date: e.target.value})}
              className="mt-1"
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={handleLogTime} className="flex-1">
              Log Time
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeLogModal;
